import { createElement, forwardRef, type ComponentRef, type ElementType, type ForwardedRef } from 'react';
import { twJoin } from 'tailwind-merge';
import { BaseInputIcon } from '@/core/tamagoshiTailwind/components/Input/utils/BaseInputComponents';
import { DISABLED_CLASSES, mergeWithDefaults, ROUNDED_VALUES, STATUS_BORDER_CLASSES, STATUS_STYLES, type AllOptionalProps, type BaseInputElementType, type BaseInputProps, type BaseInputSettings } from '@/core/tamagoshiTailwind/components/Input/utils/BaseInputTypes';
import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';
export function createBaseInput<T extends ElementType, S extends BaseInputSettings<T>>(inputSettings: S): BaseInputElementType<T, S> {
  const settings = mergeWithDefaults(inputSettings);
  const baseInputClassName = twJoin(`peer h-xxxl w-full overflow-hidden text-ellipsis whitespace-nowrap border
    bg-foundation-inverted px-m text-body2 text-foundation-primary
    active:border-foundation-tertiary`, settings.detectEmpty && DISABLED_CLASSES, `disabled:border-foundation-disabled disabled:bg-foundation-disabled
    disabled:text-foundation-disabled`, STATUS_BORDER_CLASSES, ROUNDED_VALUES[settings.rounded]);
  const baseLabelClassName = twJoin(`pointer-events-none absolute left-0 right-m top-0 translate-x-[17px]
    cursor-default select-none text-small leading-body2
    text-foundation-secondary transition-all
    peer-placeholder-shown:translate-y-m peer-placeholder-shown:cursor-text
    peer-placeholder-shown:text-body2 peer-focus:cursor-default
    peer-focus:text-small`, 'overflow-hidden overflow-ellipsis whitespace-nowrap', settings.detectEmpty && 'peer-empty:translate-y-m peer-empty:cursor-text peer-empty:text-body2', settings.detectEmpty && 'group-data-[disabled]/container:text-foundation-disabled', `translate-y-xs peer-placeholder-shown:translate-y-[14px]
    peer-focus:translate-y-xs`);
  function InnerBaseInput({
    startAddon,
    startIcon,
    endIcon,
    children,
    rootRef: containerRef,
    inputContainerRef,
    className,
    forceHoverState = false,
    ...inputProps
  }: BaseInputProps<T, S>, ref: ForwardedRef<ComponentRef<T>>) {
    const {
      status = 'default',
      helperText,
      label,
      placeholder,
      isEmpty,
      ...props
    } = inputProps as BaseInputProps<T, S> & AllOptionalProps;
    const {
      textColor,
      icon: Icon
    } = STATUS_STYLES[status];
    const effectiveEndIcon = endIcon ?? (status === 'success' && Icon && <BaseInputIcon as="div" icon={Icon} color={textColor} />);
    const disabled = status === 'disabled';
    const haveWrapper = Boolean(startAddon);
    return <label className="group/container flex w-full flex-col flex-wrap" ref={containerRef} data-status={status} data-disabled={disabled ? true : undefined} data-force-hover-state={forceHoverState ? true : undefined}>
        <div className="group/wrapper group/hover flex w-full flex-row" ref={inputContainerRef}>
          {startAddon}
          <div className={twJoin('group relative w-full', !haveWrapper && 'group/hover')}>
            {startIcon}
            {createElement(inputSettings.as ?? 'input', {
            ...props,
            className: twMerge(baseInputClassName, !placeholder && 'placeholder-transparent', label ? 'pb-[6.5px] pt-[18.5px]' : 'px-m py-s', placeholder && `placeholder-foundation-secondary
                  disabled:placeholder-foundation-disabled`, startIcon && 'pl-xxxl', effectiveEndIcon && 'pr-xxl', className),
            ref,
            placeholder: placeholder || label,
            disabled
          })}
            {label && <span className={twMerge(baseLabelClassName, startIcon && 'left-xl', effectiveEndIcon && 'right-[55px]', settings.detectEmpty && isEmpty && 'translate-y-m cursor-text text-body2', label && placeholder && `peer-placeholder-shown:translate-y-xs
                    peer-placeholder-shown:text-small
                    peer-placeholder-shown:leading-body2`, 'peer-disabled:text-foundation-disabled')}>
                {label}
              </span>}
            {children}
            {effectiveEndIcon}
          </div>
        </div>
        {helperText && <Helper color={textColor} status={status} text={helperText ?? ''} icon={Icon} />}
      </label>;
  }
  return forwardRef(InnerBaseInput) as unknown as BaseInputElementType<T, S>;
}
type HelperProps<Status extends AllOptionalProps['status']> = {
  color: string;
  status: Status;
  text: string;
  icon?: ElementType;
};
function Helper<Status extends AllOptionalProps['status']>({
  color,
  status,
  text,
  icon: Icon
}: HelperProps<Status>) {
  return <div className={twJoin('mt-xs flex gap-x-xs text-justify text-small leading-small', color)}>
      {status === 'error' && Icon && <Icon className="size-[18px]" />}
      {text}
    </div>;
}